import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  ItemDetailBoxFragmentDoc,
  PortfolioConnectionFragmentDoc,
  ResaleCreateQuery,
  ResaleCreateQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { convertPortfolioItemToSku, decodeSkuId, setItemSku } from '@/core/sku'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{
    itemId: string
    skuId?: string
  }>()
  const itemId = router.query.itemId
  const skuId = router.query?.skuId
  const { data, loading } = useQuery<
    ResaleCreateQuery,
    ResaleCreateQueryVariables
  >(QUERY, {
    variables: {
      id: itemId,
      skuId: decodeSkuId(skuId ?? '') || undefined,
    },
  })

  const portfolio = useMemo(() => {
    if (!skuId) {
      return data?.portfolio
    }
    return convertPortfolioItemToSku(data?.portfolio ?? null, skuId)
  }, [data?.portfolio, skuId])

  const item = useMemo(() => {
    if (!skuId) {
      return data?.item
    }
    return setItemSku(data?.item, skuId)
  }, [data?.item, skuId])

  const isResaleAvailable = useMemo(() => {
    if (!skuId) {
      return portfolio?.item.isResaleAvailable
    }
    return portfolio?.item.skuIsResaleAvailable
  }, [skuId, portfolio])

  return {
    state: {
      item: item ?? null,
      itemId,
      skuId,
      loading,
      serials: portfolio?.serials ?? [],
      unusedCount:
        portfolio?.serials?.filter(
          serial => !!serial.serial?.ticketStatus?.canUseTicket
        ).length ?? 0,
      isResaleAvailable: isResaleAvailable,
    },
    actions: {},
  }
})

const QUERY = gql`
  query ResaleCreate($id: ID!, $skuId: String) {
    item(id: $id) {
      ...ItemDetailBox
    }
    portfolio(id: $id) {
      ...PortfolioConnection
    }
  }
  ${ItemDetailBoxFragmentDoc}
  ${PortfolioConnectionFragmentDoc}
`
